import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardStats, getAbandonedSessions, continueSession, cancelSession, extendSession, getConflicts } from '@/lib/api';
import { useWMSEvents } from '@/lib/websocket';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Package, MapPin, PackagePlus, ScanLine, AlertTriangle, Clock, TrendingUp, Boxes, Play, XCircle, RefreshCw, User, AlertOctagon, ArrowRight } from 'lucide-react';
import { CATEGORY_COLORS, CATEGORY_LABELS, STAT_CARD_COLORS, ACTIVITY_COLORS } from '@/lib/constants';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import DraftPalletsCard from '@/components/DraftPalletsCard';

// Chart colors for categories
const CHART_COLORS = {
  bolt: '#1a56db',
  nut: '#7e3af2',
  washer: '#0891b2',
  stud: '#d97706',
  screw: '#059669',
  rod: '#dc2626',
  pin: '#374151',
  other: '#6b7280',
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [abandonedSessions, setAbandonedSessions] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAbandonedDialog, setShowAbandonedDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
    loadAbandonedSessions();
    loadConflicts();
  }, []);

  const loadStats = async () => {
    try {
      const res = await getDashboardStats();
      setStats(res.data);
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAbandonedSessions = async () => {
    try {
      const res = await getAbandonedSessions();
      setAbandonedSessions(res.data || []);
    } catch (err) {
      console.error('Abandoned sessions error:', err);
    }
  };

  const loadConflicts = async () => {
    try {
      const res = await getConflicts();
      setConflicts(res.data || []);
    } catch (err) {
      console.error('Conflicts error:', err);
    }
  };

  // Real-time updates via WebSocket
  const handleStatsUpdate = useCallback(() => {
    loadStats();
  }, []);
  
  const handleConflictUpdate = useCallback(() => {
    loadConflicts();
  }, []);
  
  const handleAbandonedUpdate = useCallback(() => {
    loadAbandonedSessions();
  }, []);

  useWMSEvents({
    onStatsUpdate: handleStatsUpdate,
    onMapUpdate: handleStatsUpdate,
    onConflict: handleConflictUpdate,
    onAbandonedSession: handleAbandonedUpdate,
    pallet_placed: handleStatsUpdate,
    pallet_issued: handleStatsUpdate,
    qc_hold_placed: handleStatsUpdate,
    qc_hold_cleared: handleStatsUpdate,
    packing_started: handleStatsUpdate,
  });

  const handleContinueSession = async (palletId) => {
    setActionLoading(palletId);
    try {
      await continueSession(palletId);
      navigate(`/pack?continue=${palletId}`);
    } catch (err) {
      console.error(err);
    }
    setActionLoading(null);
  };

  const handleCancelSession = async (palletId) => {
    setActionLoading(palletId);
    try {
      await cancelSession(palletId);
      await loadStats();
      await loadAbandonedSessions();
    } catch (err) {
      console.error(err);
    }
    setActionLoading(null);
  };

  const handleExtendSession = async (palletId) => {
    setActionLoading(palletId);
    try {
      await extendSession(palletId);
      await loadAbandonedSessions();
    } catch (err) {
      console.error(err);
    }
    setActionLoading(null);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-[#1a2fce] border-t-transparent rounded-full" /></div>;

  const summaryCards = [
    { label: 'Pallets Placed', value: stats?.total_placed || 0, icon: Package, colorKey: 'pallets_placed', href: '/inventory?status=placed' },
    { label: 'Slots Used', value: `${stats?.slots_used || 0} / ${stats?.total_slots || 764}`, icon: MapPin, colorKey: 'slots_used', href: '/map' },
    { label: 'Empty Slots', value: stats?.empty_slots || 0, icon: Boxes, colorKey: 'empty_slots', href: '/map?highlight=empty' },
    { label: 'Pending Placement', value: stats?.total_ready || 0, icon: Clock, colorKey: 'pending_placement', href: '/inventory?status=ready' },
    { label: 'QC Hold', value: stats?.total_qc_hold || 0, icon: AlertTriangle, colorKey: 'qc_hold', href: '/inventory?status=qc_hold' },
    { label: 'In Packing', value: stats?.total_packing || 0, icon: PackagePlus, colorKey: 'in_packing', href: '/inventory?status=packing' },
  ];

  const categoryData = (stats?.categories || []).map(c => ({
    name: CATEGORY_LABELS[c.name] || c.name,
    count: c.count,
    color: CHART_COLORS[c.name] || CHART_COLORS.other,
  }));

  const getActivityBadgeColor = (type) => {
    return ACTIVITY_COLORS[type] || ACTIVITY_COLORS.packing_started;
  };

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => navigate('/pack')} className="h-10 text-sm font-semibold" style={{ backgroundColor: '#1a2fce' }} data-testid="quick-new-session">
          <PackagePlus size={16} className="mr-1.5" /> New Packing Session
        </Button>
        <Button onClick={() => navigate('/scan')} variant="outline" className="h-10 text-sm font-semibold border-[#1a2fce] text-[#1a2fce]" data-testid="quick-scan">
          <ScanLine size={16} className="mr-1.5" /> Scan QR
        </Button>
        <Button onClick={() => navigate('/map')} variant="outline" className="h-10 text-sm font-semibold" data-testid="quick-map">
          <MapPin size={16} className="mr-1.5" /> Warehouse Map
        </Button>
      </div>

      {/* In-progress / Draft pallets — shown prominently so users can resume */}
      <DraftPalletsCard onlyMine={false} />

      {/* Summary Cards with colored left border */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {summaryCards.map((card) => {
          const color = STAT_CARD_COLORS[card.colorKey] || '#1a2fce';
          return (
            <Card 
              key={card.colorKey} 
              className="border-0 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer overflow-hidden group"
              style={{ 
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
              onClick={() => navigate(card.href)}
              data-testid={`stat-${card.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <CardContent className="p-4 relative">
                <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: color }} />
                <div className="flex items-center gap-2 mb-2 pl-2">
                  <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${color}15` }}>
                    <card.icon size={16} style={{ color: color }} />
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900 pl-2">{card.value}</div>
                <div className="text-xs text-[#5f6f82] mt-0.5 pl-2">{card.label}</div>
                <ArrowRight size={12} className="absolute bottom-3 right-3 text-gray-300 group-hover:text-gray-500 transition-colors" />
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Category Breakdown with specific colors */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800">Category Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={categoryData} layout="vertical" margin={{ left: 70 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {categoryData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 text-gray-400 text-sm">No items in warehouse yet</div>
            )}
          </CardContent>
        </Card>

        {/* Top Space Blockers */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <TrendingUp size={16} className="text-[#e8191a]" /> Top Space Blockers
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.top_space_blockers || []).length > 0 ? (
              <div className="space-y-2">
                {stats.top_space_blockers.slice(0, 5).map((b, i) => (
                  <div
                    key={b.pallet_ref}
                    onClick={() => navigate(`/pallet/${b.pallet_ref}`)}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <span className="text-xs font-bold text-gray-400 w-5">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-mono font-semibold text-gray-900">{b.pallet_ref}</div>
                      <div className="text-xs text-gray-500 truncate">{b.items?.[0]?.description}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-semibold text-[#e8191a]">Score: {b.score}</div>
                      <div className="text-xs text-gray-400">{b.days}d · {b.ctns} ctns</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400 text-sm">No pallets placed yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sync Conflicts Alert */}
      {conflicts.length > 0 && (
        <Card 
          className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
          style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          onClick={() => navigate('/map')}
          data-testid="alert-conflicts"
        >
          <CardContent className="p-4 flex items-center gap-3 relative">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500" />
            <div className="pl-2">
              <AlertOctagon size={24} className="text-amber-500" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-800">
                {conflicts.length} Slot Conflict{conflicts.length > 1 ? 's' : ''} Detected
              </div>
              <div className="text-xs text-gray-500">
                {conflicts.map(c => c.slot_address).join(', ')} — Multiple pallets assigned. Please resolve.
              </div>
            </div>
            <Badge className="bg-amber-100 text-amber-700 border-amber-200">Resolve Now</Badge>
          </CardContent>
        </Card>
      )}

      {/* Abandoned Sessions Alert */}
      {abandonedSessions.length > 0 && (
        <Card 
          className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
          style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          onClick={() => setShowAbandonedDialog(true)}
          data-testid="alert-abandoned"
        >
          <CardContent className="p-4 flex items-center gap-3 relative">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500" />
            <div className="pl-2">
              <AlertTriangle size={24} className="text-amber-500" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-800">
                {abandonedSessions.length} Abandoned Session{abandonedSessions.length > 1 ? 's' : ''}
              </div>
              <div className="text-xs text-gray-500">
                Packing sessions open for more than the threshold time. Click to manage.
              </div>
            </div>
            <Badge className="bg-amber-100 text-amber-700 border-amber-200">Action Required</Badge>
          </CardContent>
        </Card>
      )}

      {/* Abandoned Sessions Management Dialog */}
      <Dialog open={showAbandonedDialog} onOpenChange={setShowAbandonedDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-amber-500" size={20} />
              Abandoned Sessions
            </DialogTitle>
            <DialogDescription>
              These packing sessions have been open beyond the configured threshold. Choose an action for each.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {abandonedSessions.map((session) => (
              <Card key={session.id} className="border border-amber-200 bg-amber-50/50">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono font-bold text-gray-900">{session.pallet_ref}</span>
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">
                          {session.hours_open}h open
                        </Badge>
                      </div>
                      <div className="text-xs text-gray-600 space-y-0.5">
                        <div className="flex items-center gap-1">
                          <User size={10} /> Started by: {session.created_by_name || 'Unknown'}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock size={10} /> Started: {new Date(session.created_at).toLocaleString()}
                        </div>
                        <div>Items entered: {session.items_count || 0}</div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-green-600 hover:bg-green-700"
                        onClick={() => handleContinueSession(session.id)}
                        disabled={actionLoading === session.id}
                      >
                        <Play size={12} className="mr-1" /> Continue
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-amber-400 text-amber-600 hover:bg-amber-50"
                        onClick={() => handleExtendSession(session.id)}
                        disabled={actionLoading === session.id}
                      >
                        <RefreshCw size={12} className="mr-1" /> Extend
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-red-400 text-red-600 hover:bg-red-50"
                        onClick={() => handleCancelSession(session.id)}
                        disabled={actionLoading === session.id}
                      >
                        <XCircle size={12} className="mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Recent Activity with colored badges */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-gray-800">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {(stats?.recent_activity || []).length > 0 ? (
            <div className="space-y-2">
              {stats.recent_activity.map((t) => (
                <div key={t.id || t.timestamp} className="flex items-center gap-3 text-sm py-1.5 border-b border-gray-50 last:border-0">
                  <Badge 
                    className="text-[10px] font-medium text-white border-0 shrink-0"
                    style={{ backgroundColor: getActivityBadgeColor(t.type) }}
                  >
                    {t.type?.replace(/_/g, ' ')}
                  </Badge>
                  <span className="text-gray-500 text-xs truncate flex-1">{t.notes || t.type}</span>
                  <span className="text-xs text-gray-400 shrink-0">{t.timestamp ? new Date(t.timestamp).toLocaleTimeString() : ''}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400 text-sm">No recent activity</div>
          )}
        </CardContent>
      </Card>

      {/* Footer branding */}
      <div className="text-center text-xs text-gray-400 py-2">
        Sturdfix™ WMS · Singhania International Limited
      </div>
    </div>
  );
}
